import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { useErrors } from '../../hooks/useErrors.js';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { useSelector } from 'react-redux';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import { PERMISSION_FILES } from '../../utils/constants.js';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useEditFileMutation, useGetFileQuery } from '../../api/file.js';
import { FILE_SCHEMA } from '../../utils/file.js';
import FileDeleteConfirmation from '../../components/misc/FileDeleteConfirmation.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import { useGetCountriesQuery } from '../../api/misc.js';

const EditFile = () => {
    let file = null;
    const { permissions: userPermissions } = useCurrentUser();
    const [deleteVisible, setDeleteVisible] = useState(false);
    const user = useSelector((state) => state.user.user);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const navigate = useNavigate();
    const { id } = useParams();

    const [isPrivate, setIsPrivate] = useState(true);

    if (id) {
        const { data: fileResponse } = useGetFileQuery(id);

        file = useMemo(() => {
            if (fileResponse?.response?.file) {
                return fileResponse.response.file;
            }

            return null;
        }, [fileResponse]);
    } else {
        return null;
    }

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;

        user.organizations.forEach((item) => {
            const permission = user.role.permissions.find(
                (value) => value.organization_id === item.id && value.permission === PERMISSION_FILES
            );

            const hasPermission = permission && permission.update;

            if (hasPermission) {
                setPermittedOrganizations((prevState) => {
                    if (!prevState.some((org) => org.id === item.id)) {
                        return [...prevState, item];
                    }
                    return prevState;
                });
            }
        });
    }, [user]);

    const [editContact, { isLoading: isLoadingEdit, error: errorEdit }] = useEditFileMutation();

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    const { handleSubmit, getValues, formState, control, clearErrors, reset, setValue, watch } = useForm({
        resolver: yupResolver(FILE_SCHEMA),
        defaultValues: {
            title: '',
            description: '',
            shared: false,
            shared_organizations: [],
            shared_users: [],
            country_id: ''
        },
        reValidateMode: 'onSubmit'
    });

    const { errors } = useErrors(errorEdit?.message, formState);
    const sharedOrganizations = watch('shared_organizations');
    const sharedUsers = watch('shared_users');

    const handleSharedOrganizations = (index, value) => {
        if (permittedOrganizations.length > 0) {
            const organizationId = permittedOrganizations[index].id;

            const updatedSharedOrganizations = value
                ? [...sharedOrganizations, organizationId]
                : sharedOrganizations.filter((orgId) => orgId !== organizationId);

            setValue('shared_organizations', updatedSharedOrganizations);

            if (updatedSharedOrganizations.length === 0) {
                handleSharedUsers(true);
            }
        }
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length > 0) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const updatedSharedUsers = value ? [...sharedUsers, id] : sharedUsers.filter((id) => id !== id);

        setValue('shared_users', updatedSharedUsers);
    };

    const renderShared = () => {
        if (permittedOrganizations.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }

        return (
            <div>
                {permittedOrganizations.map(({ id, name }, index) => (
                    <SharedModelListItem
                        key={id}
                        index={index}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        isLoading={isLoadingEdit}
                        disabled={isPrivate}
                    />
                ))}

                <PrivateUsersListItem
                    key='separate-item'
                    text={'Private'}
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={file?.shared_users || []}
                    isLoading={isLoadingEdit}
                    permission={PERMISSION_FILES}
                />
            </div>
        );
    };

    const handleDeleteConfirmation = () => {
        setDeleteVisible(true);
    };

    const sendEditContact = async (requestData) => {
        const updatedData = {
            ...requestData,
            title: requestData.title,
            shared: requestData.shared_organizations.length > 0,
            country_id: requestData.country_id
        };

        await editContact({ id: id || '', data: updatedData }).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/files');
            }
        });
    };

    const formValues = getValues();

    useEffect(() => {
        if (file) {
            if (!canUpdateOrDelete(user, file, PERMISSION_FILES, 'update')) {
                navigate('/unauthorized');
                return;
            }
            const newValues = {
                title: file.title,
                description: file.description || '',
                country_id: file.country_id,
                shared: file.shared,
                shared_organizations: file.shared_organizations,
                shared_users: file.shared_users.map((shareUser) => shareUser.id)
            };

            reset(newValues);
            setIsPrivate(!file.shared);
        }
    }, [file, navigate, reset, user]);

    return (
        <AuthLayout title={'Edit File'}>
            <div className='max-w-3xl w-full mx-auto px-8'>
                <Accordion className={`custom-accordion mt-2 mb-2`} multiple={false} activeIndex={null}>
                    <AccordionTab
                        key='shared-within'
                        header={<span className='text-lg font-bold text-theme1-text2'>Shared within</span>}
                    >
                        {renderShared()}
                    </AccordionTab>
                </Accordion>
            </div>
            <div className='max-w-3xl w-full mx-auto px-8'>
                <div className='px-6 bg-theme1-white border rounded-3xl'>
                    <div className='pt-5 mb-4 text-theme1-primary font-bold text-xl'>File information</div>
                    <div className='mb-10'>
                        <BaseInput
                            type='text'
                            control={control}
                            label='Title'
                            name='title'
                            clearErrors={clearErrors}
                            errors={[...(errors?.title ? errors.title : [])]}
                        />
                        <BaseInput
                            className='mt-4'
                            type='text'
                            control={control}
                            label='Description'
                            name='description'
                            clearErrors={clearErrors}
                            errors={[]}
                        />
                        <BaseDropdown
                            className='mt-4'
                            control={control}
                            label='Country'
                            name='country_id'
                            clearErrors={clearErrors}
                            errors={[]}
                            options={countries}
                        />
                    </div>
                    <div className='mb-6'>
                        <div className='grid grid-cols-2 gap-4'>
                            <BaseButton
                                id='btn-submit'
                                className='btn-primary-outline'
                                onClick={handleSubmit(sendEditContact)}
                                isLoading={isLoadingEdit}
                                textValue='Save'
                                disabled={!formValues.title}
                            />

                            <BaseButton
                                id='btn-cancel'
                                className='btn-primary-outline'
                                onClick={() => navigate('/files')}
                                textValue='Cancel'
                            />
                        </div>
                    </div>

                    {!!userPermissions[PERMISSION_FILES].delete &&
                        file &&
                        canUpdateOrDelete(user, file, PERMISSION_FILES, 'delete') && (
                            <div className='flex flex-col max-w-3xl w-full mx-auto mb-6'>
                                <div className='flex flex-col justify-center items-center'>
                                    <BaseButton
                                        className='btn-red'
                                        onClick={handleDeleteConfirmation}
                                        textValue={'Delete'}
                                    />
                                </div>
                            </div>
                        )}
                </div>
            </div>

            {deleteVisible && (
                <FileDeleteConfirmation deleteVisible={deleteVisible} setDeleteVisible={setDeleteVisible} file={file} />
            )}
        </AuthLayout>
    );
};

export default EditFile;
